import React from 'react';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { money } from './portalApi';

const PILL = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-purple-50 text-purple-700 border-purple-200',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  tentative: 'bg-amber-50 text-amber-800 border-amber-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
  valid: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  expiring: 'bg-amber-50 text-amber-800 border-amber-300',
  expired: 'bg-rose-50 text-rose-700 border-rose-300',
  live: 'bg-emerald-500 text-white border-emerald-500 shadow-sm ring-2 ring-emerald-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  new: 'bg-rose-50 text-rose-700 border-rose-200',
  acknowledged: 'bg-amber-50 text-amber-800 border-amber-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
  info: 'bg-slate-100 text-slate-700 border-slate-200',
  navy: 'bg-[#0B192C] text-white border-[#0B192C]',
};
export const Pill = ({ tone = 'info', children, className = '', testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 text-[11px] font-bold tracking-wide uppercase px-2.5 py-0.5 rounded-full border whitespace-nowrap ${PILL[tone] || PILL.info} ${className}`}>{children}</span>
);

export const PageHeader = ({ overline, title, subtitle, actions, testId }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7 p-rise" data-testid={testId}>
    <div>
      {overline && <div className="p-overline mb-1">{overline}</div>}
      <h1 className="p-display text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">{title}</h1>
      {subtitle && <p className="text-sm sm:text-base text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2 shrink-0">{actions}</div>}
  </div>
);

export const Card = ({ children, className = '', hover = false, testId, ...rest }) => (
  <div data-testid={testId} className={`p-card min-w-0 ${hover ? 'p-card-hover' : ''} ${className}`} {...rest}>{children}</div>
);

export const CardHead = ({ title, subtitle, right, icon: Icon }) => (
  <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
    <div className="flex items-start gap-3 min-w-0">
      {Icon && <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 shrink-0"><Icon className="w-4 h-4" /></span>}
      <div className="min-w-0">
        <h3 className="text-base sm:text-lg font-bold tracking-tight text-slate-900 truncate">{title}</h3>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {right && <div className="shrink-0">{right}</div>}
  </div>
);

const KPI_TONE = {
  emerald: 'from-emerald-500/15 to-transparent text-emerald-700',
  blue: 'from-blue-500/15 to-transparent text-blue-700',
  slate: 'from-slate-500/15 to-transparent text-slate-700',
  amber: 'from-amber-500/15 to-transparent text-amber-700',
  purple: 'from-purple-500/15 to-transparent text-purple-700',
  rose: 'from-rose-500/15 to-transparent text-rose-700',
};
export const Kpi = ({ label, value, sub, badge, tone = 'slate', icon: Icon, testId, className = '', to }) => {
  const body = (
    <Card hover className={`relative overflow-hidden p-5 h-full ${className}`} testId={testId}>
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tone === 'emerald' ? 'from-emerald-500 to-emerald-300' : tone === 'blue' ? 'from-blue-600 to-blue-400' : tone === 'amber' ? 'from-amber-500 to-amber-300' : tone === 'purple' ? 'from-purple-600 to-purple-400' : tone === 'rose' ? 'from-rose-600 to-rose-400' : 'from-slate-600 to-slate-400'}`} />
      <div className="flex items-start justify-between gap-2">
        <div className="p-overline">{label}</div>
        {Icon && <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br ${KPI_TONE[tone]}`}><Icon className="w-4 h-4" /></span>}
      </div>
      <div className="p-mono text-2xl font-bold text-slate-900 mt-2 leading-none break-words">{value}</div>
      <div className="flex items-start justify-between gap-2 mt-2.5">
        <div className="text-xs text-slate-500 leading-snug">{sub}</div>
        {badge && <div className="shrink-0">{badge}</div>}
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
};

export const Empty = ({ icon: Icon = Inbox, title, text, action, testId }) => (
  <div className="text-center py-14 px-6" data-testid={testId}>
    <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3"><Icon className="w-6 h-6" /></div>
    <p className="font-semibold text-slate-800">{title}</p>
    {text && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Money = ({ v, className = '' }) => <span className={`p-mono ${className}`}>{money(v)}</span>;

export const Skeleton = ({ className = '' }) => <div className={`animate-pulse rounded-lg bg-slate-200/70 ${className}`} />;

export const WoChip = ({ wo, title }) => (
  <span className="inline-flex items-baseline gap-1.5 min-w-0">
    <span className="p-mono text-[13px] font-bold text-blue-700">{wo}</span>
    {title && <span className="text-slate-500 text-sm truncate">— {title}</span>}
  </span>
);

export const Spinner = () => <div className="flex justify-center py-16"><div className="h-9 w-9 rounded-full border-[3px] border-slate-200 border-t-blue-600 animate-spin" /></div>;

export const Modal = ({ title, subtitle, onClose, children, wide = false, testId }) => {
  React.useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid={testId}>
      <div className="absolute inset-0 bg-[#0B192C]/60 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`relative w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'} max-h-[92vh] flex flex-col bg-white rounded-t-2xl sm:rounded-2xl border border-slate-200 shadow-2xl p-rise`}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 leading-tight truncate">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 text-slate-500 text-xl leading-none shrink-0" aria-label="Close" data-testid="portal-modal-close">×</button>
        </div>
        <div className="overflow-y-auto p-scroll px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

export const Tabs = ({ tabs, value, onChange, testPrefix = 'tab' }) => (
  <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1">
    {tabs.map((t) => (
      <button key={t.key} type="button" onClick={() => onChange(t.key)} data-testid={`${testPrefix}-${t.key}`}
        className={`px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors ${value === t.key ? 'bg-[#0B192C] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
        {t.label}{t.count != null && <span className={`ml-1.5 text-[11px] p-mono ${value === t.key ? 'text-blue-200' : 'text-slate-400'}`}>{t.count}</span>}
      </button>
    ))}
  </div>
);
